import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useAppContext } from '../../../redux/slices/appContext'
import { useGetStatusReportById, useLatestClientStatusReport } from '../../../api/groups'

const StatusReportContext = createContext({})

export const useStatusReportContext = () => {
  return useContext(StatusReportContext)
}

function StatusReportContextProvider ({ children, config, variant, templateId, templatePurposeKey }) {
  const { clientId } = useAppContext()
  const [currentFilter, setCurrentFilter] = useState(null)
  const [reportExpanded, setReportExpanded] = useState(false)
  const [selectedStatusReportId, setSelectedStatusReportId] = useState(null)
  const setFilter = useCallback((value) => {
    setCurrentFilter(value)
  }, [setCurrentFilter])
  const clearFilter = useCallback((value) => {
    setCurrentFilter(null)
  }, [setCurrentFilter])

  const [selectedStatusReportItemId, setSelectedStatusReportItemId] = useState(null)

  // Fetch report data at provider level
  const useSpecificReport = !!selectedStatusReportId

  const latestQuery = useLatestClientStatusReport(clientId, {
    templateId,
    templatePurposeKey,
    format: 'tree'
  }, {
    enabled: !!clientId && !useSpecificReport
  })

  const specificQuery = useGetStatusReportById(selectedStatusReportId, {
    format: 'tree'
  }, {
    enabled: useSpecificReport
  })

  const { data, isLoading, refetch, isFetching } = useSpecificReport ? specificQuery : latestQuery

  useEffect(() => {
    setSelectedStatusReportItemId(null)
  }, [data?.report?.statusReportId, selectedStatusReportId]) // Only trigger when report ID changes, not on every data update

  const contextManager = useMemo(() => {
    return {
      // Data
      report: data?.report,
      statuses: data?.statuses,
      isLoading,
      isFetching,
      refetch,
      // Configuration
      currentFilter,
      clearFilter,
      setFilter,
      config,
      variant,
      templateId,
      templatePurposeKey,
      // Version selection
      selectedStatusReportId,
      setSelectedStatusReportId,
      // Filter functions
      toggleFilter: (statusId) => {
        if (currentFilter === statusId) {
          clearFilter()
        } else {
          setFilter(statusId)
        }
      },
      filter: (depth) => (statusId) => {
        if (currentFilter === null || currentFilter === undefined) return true

        if (config?.filtersAt?.includes(depth)) {
          return statusId === currentFilter
        }
        return true
      },
      // Item selection
      selectedStatusReportItemId,
      setSelectedStatusReportItemId
    }
  }, [data, isLoading, isFetching, refetch, currentFilter, clearFilter, setFilter, config, variant, templateId, templatePurposeKey, selectedStatusReportId, selectedStatusReportItemId])

  return (
    <StatusReportContext.Provider value={{ ...contextManager, reportExpanded, setReportExpanded }}>
      {children}
    </StatusReportContext.Provider>
  )
}

StatusReportContextProvider.propTypes = {
  children: PropTypes.node,
  config: PropTypes.shape({
    filtersAt: PropTypes.arrayOf(PropTypes.number),
    countItemsAt: PropTypes.arrayOf(PropTypes.number),
    supplementalDataCode: PropTypes.string,
    allItemsLabel: PropTypes.string
  }),
  variant: PropTypes.oneOf(['default', 'board']),
  templateId: PropTypes.number,
  templatePurposeKey: PropTypes.string
}

StatusReportContextProvider.defaultProps = {
  variant: 'default'
}

export default StatusReportContextProvider
